import { ArrowLeft, Check, Minus, Plus, ReceiptTextIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { useProducts } from "@/api/products-query";
import { ExpandingSearchBar } from "@/components/dashboard/search-bar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn, formatCurrency } from "@/lib/utils";
import { useContractCartStore } from "@/store/contract-cart";
import { Label } from "@radix-ui/react-label";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CreateContractDialog } from "./cart";

export const Route = createFileRoute("/vendor/contract/create")({
	component: RouteComponent
});

function RouteComponent() {
	const [showCart, setShowCart] = useState(false);
	const [newPrices, setNewPrices] = useState<Record<string, number>>({});
	const handlePriceChange = (id: string, price: number) => {
		setNewPrices(prev => ({
			...prev,
			[id]: price
		}));
	};

	return (
		<section>
			{showCart
				? <CartListingComponent toggleShowCart={() => setShowCart(false)} handlePriceChange={handlePriceChange} />
				: (
						<ProductListing toggleShowCart={() => setShowCart(true)} updatePrices={newPrices} />
					)}
		</section>
	);
}

function ProductListing({ toggleShowCart }: { toggleShowCart: () => void; updatePrices: Record<string, number> }) {
	const [value, setValue] = useState("");
	const { data, isPending } = useProducts();
	const cartItems = useContractCartStore(state => state.items);
	const manageItems = useContractCartStore(state => state.manageItems);
	const getTotalItems = useContractCartStore(state => state.getTotalItems);

	const totalItems = getTotalItems(cartItems);
	const products = useMemo(() => {
		if (!data)
			return [];
		return data.items.filter(product => product.name.toLowerCase().includes(value.toLowerCase()));
	}, [data, value]);
	return (
		<div className="flex flex-col space-y-4">
			<div className="flex  items-center gap-2">
				<ExpandingSearchBar placeholder="Search products" className="ml-auto" value={value} onChange={e => setValue(e.target.value)} />
				<Button variant="secondary" className="relative border p-2 overflow-hidden" onClick={toggleShowCart}>
					{!!totalItems && <span className="absolute flex items-center justify-center h-fit min-w-4 min-h-4 w-fit rounded-full bg-red-500 top-1 right-4 text-xs text-white transform translate-x-1/2">{cartItems.length}</span>}
					<ReceiptTextIcon className="h-4 w-6" />
				</Button>
				<Button disabled={totalItems <= 0} onClick={toggleShowCart}>
					Generate Contract
				</Button>
			</div>

			<section className={cn("min-h-[80vh] p-3 bg-gray-50 rounded-xl flex flex-col gap-4 flex-wrap h-full items-center justify-center", {
				"items-start justify-start": products.length
			})}
			>
				{
					isPending
						? (
								<div className="flex flex-col items-center justify-center gap-4">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
									<p className="text-muted-foreground">Loading...</p>
								</div>
							)
						: products.length
							? (
									<div className="w-full grid grid-cols-[repeat(auto-fit,_minmax(250px,_250px))] max-sm:grid-cols-1 gap-2">
										{products.map((product) => {
											const existingItem = cartItems.find(items => items.product.id === product.id);
											return (
												<Card
													key={product.id}
													className="p-2 space-y-4 bg-gray-100 shadow-none"
												>
													<CardHeader className="w-full h-40 bg-white rounded-xl overflow-hidden">
														<img src={product.photos.length ? product.photos[0] : "/placeholder.svg"} className="object-cover w-full h-full" alt={product.name} />
													</CardHeader>
													<CardContent className="p-1 text-ellipsis overflow-hidden">
														<div className="flex items-center justify-between">
															<div className="flex flex-col">
																<CardTitle className="capitalize overflow-hidden whitespace-nowrap text-ellipsis">
																	{product.name}
																</CardTitle>
																<Button asChild variant="link" className="p-0 m-0 justify-start">
																	<Link
																		to="/vendor/store/$productid"
																		params={{ productid: product.id }}
																		key={product.id}
																	>
																		more details
																	</Link>
																</Button>
															</div>
															<div className="flex items-center gap-2">
																<Button
																	variant="secondary"
																	onClick={() => {
																		!existingItem ? manageItems(product, "add") : manageItems(product, "reduce");
																	}}
																	className="bg-hover-secondary hover:bg-primary/40 rounded-full size-6 p-0 m-0 text-primary"
																>
																	{!existingItem ? <Plus className="h-4 w-6" /> : <Check className="h-4 w-6" />}
																</Button>
															</div>
														</div>
													</CardContent>
												</Card>
											);
										})}
									</div>
								)
							: (
									<div className="space-y-4 *:text-center">
										<div className="m-auto flex items-center justify-center bg-hover-secondary size-48 rounded-full">
											<img src="/no-products.png" alt="contract icon" />
										</div>
										<div>
											<h3 className="text-lg font-bold">
												{
													data?.items.length ? "Product Not Found" : "You've not added any products yet"
												}
											</h3>
											{!data?.items.length && <p className="text-muted-foreground">Add a list of your products and they will appear here</p>}
										</div>
									</div>
								)
				}
			</section>
		</div>
	);
}

function CartListingComponent({ toggleShowCart, handlePriceChange }: { toggleShowCart: () => void; handlePriceChange: (id: string, price: number) => void }) {
	const cart = useContractCartStore(state => state.items);
	const manageItems = useContractCartStore(state => state.manageItems);
	const resetCart = useContractCartStore(state => state.resetCart);
	const removeItem = useContractCartStore(state => state.removeItem);
	const getTotalItems = useContractCartStore(state => state.getTotalItems);

	const totalItems = getTotalItems(cart);

	const [price, setPrice] = useState<number | null>();

	return (
		<section className="space-y-2">
			<div className="flex gap-2">
				<Button variant="secondary" size="icon" className="mr-auto" onClick={toggleShowCart}>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<Button variant="accent" disabled={totalItems <= 0} onClick={resetCart}>
					Clear Cart
				</Button>
				<CreateContractDialog />
			</div>
			{totalItems > 0
				? (
						<ul className="space-y-4">
							{cart.map(item => (
								<Card key={item.product.id} className="flex flex-wrap shadow-none items-center">
									<CardHeader>
										<div className="size-24 border rounded-sm">
											<img
												src={item.product.photos[0] ?? "/placeholder.svg"}
												alt={item.product.name}
												className="object-cover w-full h-full"
											/>

										</div>
									</CardHeader>
									<CardContent className="flex flex-col h-full p-0">
										<CardTitle className="capitalize">{item.product.name}</CardTitle>
										<p>
											Price:
											{formatCurrency(Number(item.product.price), "NGN")}
										</p>
										<Button variant="link" className="justify-start px-0 bg-red-5 m-0" asChild>
											<Link to="/vendor/store/$productid" params={{ productid: item.product.id }}>See product details</Link>
										</Button>
									</CardContent>
									<CardFooter className="flex-col ml-auto mt-auto gap-4 items-end ">
										<QuantitySelector
											max={10}
											quantity={item.quantity}
											increase={() => manageItems(item.product, "add")}
											decrease={() => manageItems(item.product, "reduce")}
										/>
										<div className="flex space-x-2">
											<Dialog>
												<DialogTrigger asChild>
													<Button className="w-32">Edit</Button>
												</DialogTrigger>
												<DialogContent className="sm:max-w-[425px] space-y-4 rounded-lg">
													<DialogHeader>
														<DialogTitle>Edit Product Price</DialogTitle>
													</DialogHeader>
													<div>
														<Label htmlFor="price" className="text-right text-xs text-muted-foreground">
															Enter Amount (NGN)
														</Label>
														<Input id="price" min={1} type="number" className="col-span-3 h-14" value={price ?? ""} onChange={e => setPrice(e.target.valueAsNumber)} />
													</div>
													<DialogFooter className="sm:justify-between gap-2">
														<DialogTrigger asChild>
															<Button variant="secondary">Cancel</Button>

														</DialogTrigger>
														<Button
															type="submit"
															onClick={() => {
																if (!price)
																	return;
																handlePriceChange(item.product.id, price);
															}}
														>
															Save changes
														</Button>
													</DialogFooter>
												</DialogContent>
											</Dialog>

											<Button className="w-32" variant="accent" onClick={() => removeItem(item.product.id)}>Remove</Button>
										</div>
									</CardFooter>
								</Card>
							))}
						</ul>
					)
				: (
						<div className="bg-white min-h-[80vh] flex flex-col items-center justify-center space-y-4 text-center rounded-lg">
							<p>No Products Provided</p>
							<Button variant="accent" onClick={toggleShowCart}>
								Add Products
							</Button>
						</div>
					)}
		</section>
	);
}

interface QuantitySelectorProps {
	quantity?: number;
	min?: number;
	max?: number;
	increase: () => void;
	decrease: () => void;
}

function QuantitySelector({

	quantity = 1,
	min = 0,
	max = 99,
	increase,
	decrease
}: QuantitySelectorProps) {
	const increment = () => {
		if (quantity < max)
			increase();
	};

	const decrement = () => {
		if (quantity > min)
			decrease();
	};

	return (
		<div className="inline-flex items-center rounded-lg border bg-background">
			<Button
				variant="ghost"
				size="icon"
				onClick={decrement}
				disabled={quantity <= min}
				className="h-10 w-10 rounded-l-lg border-r text-indigo-600 hover:bg-indigo-50 hover:text-indigo-600"
			>
				<Minus className="h-4 w-4" />
			</Button>
			<div className="flex h-10 w-12 items-center justify-center text-center">
				<span className="text-sm font-medium">{quantity}</span>
			</div>
			<Button
				variant="ghost"
				size="icon"
				onClick={increment}
				disabled={quantity >= max}
				className="h-10 w-10 rounded-r-lg border-l text-indigo-600 hover:bg-indigo-50 hover:text-indigo-600"
			>
				<Plus className="h-4 w-4" />
			</Button>
		</div>
	);
}
